import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { PaletteSubtitle } from '../palette-widget/palette-widget.component';
import { CgOption } from '../cg-select/cg-select.component';
import { GRADIENT_SCHEME_MINS } from '../../service/palette/palette-generation.service';
import { WALLPAPER_SIZES, WALLPAPER_STYLES, WallpaperService, WallpaperStyle, defaultParams, styleInfoOf } from '../../service/wallpaper/wallpaper.service';

/** the dynamic wallpaper's settings, as the widget shows them */
export interface DynamicSettings {
  mode: string;
  frames: number;
  night: number;
  glow: number;
}

/** the preview canvas is drawn at most this wide (the hero backdrop is this picture, so full HD); downloads render at the preset's full size */
const PREVIEW_MAX_W = 1920;

/**
 * The wallpaper hero card, shared by /tools/wallpaper and the home page (2026-09-22):
 * the palette builder on top (the same widget the palette and gradient tools use),
 * the picture controls (style and its two fields) and the live preview under it.
 * Controlled like its siblings: it renders the `palette` + picture inputs it is
 * given and emits intent; it also draws the preview itself and hands the data
 * url up through `rendered`, so a host can paint it as a backdrop.
 */
@Component( {
  selector: 'app-wallpaper-widget',
  templateUrl: './wallpaper-widget.component.html',
  styleUrls: [ './wallpaper-widget.component.scss' ],
} )
export class WallpaperWidgetComponent implements OnChanges, AfterViewInit, OnDestroy {

  @Input() palette: { value: string }[] = [];
  @Input() subtitle: PaletteSubtitle | null = null;
  @Input() scheme = '';
  @Input() count = 6;
  @Output() countChange = new EventEmitter<number>();
  /** the card's title (the dynamic wallpaper page names its own tool) */
  @Input() cardTitle = 'Wallpaper Generator';
  /** a picture the host draws for the preview instead (the dynamic wallpaper's whole day in bands) */
  @Input() previewImage = '';

  // ----- the dynamic wallpaper's day (its page only): how it changes, the frames, the night and the glow -----
  @Input() dynamic: DynamicSettings | null = null;
  @Input() dynamicModes: { key: string; label: string; blurb: string }[] = [];
  @Input() frameCounts: number[] = [];
  @Output() dynamicChange = new EventEmitter<Partial<DynamicSettings>>();
  modeOpen = false;
  /** the host is still drawing the picture: the preview shades over with the Codigrate loader */
  @Input() previewLoading = false;

  /** each schedule's glyph, as the generator picker has one per generator */
  modeIcon( key: string ): string {
    return key === 'time' ? 'clock' : key === 'appearance' ? 'circle-half-stroke' : 'sun';
  }

  @Input() style: WallpaperStyle = 'mesh';
  @Input() sizeKey = WALLPAPER_SIZES[ 0 ].key;
  @Input() seed = 1;
  /** the style's two fields, 0..100 */
  @Input() params: number[] = defaultParams( 'mesh' );

  // Home page: a read-more style CTA to the full tool.
  @Input() showDetailsCta = false;
  @Input() detailsRoute = '';
  @Input() detailsQueryParams: { [ key: string ]: string } | null = null;

  @Output() editColor = new EventEmitter<{ index: number; value: string }>();
  @Output() reorder = new EventEmitter<CdkDragDrop<unknown>>();
  @Output() addColor = new EventEmitter<void>();
  @Output() removeColor = new EventEmitter<number>();
  @Output() reverse = new EventEmitter<void>();
  @Output() generate = new EventEmitter<string>();
  @Output() uploadImage = new EventEmitter<Event>();
  @Output() styleChange = new EventEmitter<WallpaperStyle>();
  @Output() paramsChange = new EventEmitter<number[]>();
  /** the home CTA to the full tool was clicked (the host logs it) */
  @Output() detailsClick = new EventEmitter<void>();
  /** the preview at the chosen size as a jpeg data url, after every redraw */
  @Output() rendered = new EventEmitter<string>();
  /** the same picture in the 1920 × 1080 framing (the hero backdrop and the mini preview) */
  @Output() heroRendered = new EventEmitter<string>();

  @ViewChild( 'canvas', { static: true } ) canvasRef!: ElementRef<HTMLCanvasElement>;

  GRADIENT_SCHEME_MINS = GRADIENT_SCHEME_MINS;
  styleOptions: CgOption[] = WALLPAPER_STYLES.map( s => ( { value: s.key, label: s.label } ) );
  previewUrl = '';
  /** the mini 1920x1080 preview in the card's corner */
  miniUrl = '';

  // ----- the style picker (owner 2026-10-12): a small picture of each style in the current colors -----

  readonly styles = WALLPAPER_STYLES;
  styleOpen = false;
  styleMaxHeight = 440;
  /** each style's picture in the picker: drawn once, ahead of time, not live (owner 2026-10-13: drawing all
   *  sixteen on every open slowed the page down); one palette and seed for all, so they compare */
  readonly thumbs: { [ key: string ]: string } = WALLPAPER_STYLES.reduce( ( all, s ) =>
    ( { ...all, [ s.key ]: `./assets/product-lines/tools/wallpaper-styles/${ s.key }.webp` } ), {} as { [ key: string ]: string } );

  toggleStyle( button?: HTMLElement ): void {
    this.styleOpen = !this.styleOpen;
    if ( !this.styleOpen ) {
      return;
    }
    if ( button && typeof window !== 'undefined' ) {
      this.styleMaxHeight = Math.max( 260, Math.round( window.innerHeight - button.getBoundingClientRect().bottom - 8 - 16 ) );
    }
  }

  pickStyle( key: string ): void {
    this.styleOpen = false;
    this.onStyleChange( key );
  }

  @HostListener( 'document:click', [ '$event' ] )
  onDocumentClick( event: MouseEvent ): void {
    const target = event.target as HTMLElement;
    if ( this.styleOpen && !target.closest( '.ws-pick-style' ) ) {
      this.styleOpen = false;
    }
    if ( this.modeOpen && !target.closest( '.ws-pick-mode' ) ) {
      this.modeOpen = false;
    }
  }

  // ---------------------------------------------------------------- the dynamic wallpaper's day

  get modeInfo() {
    return this.dynamicModes.find( m => m.key === this.dynamic?.mode ) || this.dynamicModes[ 0 ];
  }

  get frameOptions(): CgOption[] {
    return this.frameCounts.map( n => ( { value: String( n ), label: `${ n } frames` } ) );
  }

  toggleMode( button?: HTMLElement ): void {
    this.modeOpen = !this.modeOpen;
    if ( this.modeOpen && button && typeof window !== 'undefined' ) {
      this.styleMaxHeight = Math.max( 200, Math.round( window.innerHeight - button.getBoundingClientRect().bottom - 8 - 16 ) );
    }
  }

  pickMode( key: string ): void {
    this.modeOpen = false;
    if ( key !== this.dynamic?.mode ) this.dynamicChange.emit( { mode: key } );
  }

  onFrames( value: string ): void {
    const n = parseInt( value, 10 );
    if ( !isNaN( n ) && n !== this.dynamic?.frames ) this.dynamicChange.emit( { frames: n } );
  }

  /** Night and Glow, 0 to 100, as the style's fields */
  commitLight( key: 'night' | 'glow', raw: string ): void {
    const n = parseInt( ( raw || '' ).replace( /[^0-9]/g, '' ), 10 );
    if ( isNaN( n ) || !this.dynamic ) return;
    const value = Math.max( 0, Math.min( 100, n ) );
    if ( value !== this.dynamic[ key ] ) this.dynamicChange.emit( { [ key ]: value } );
  }

  /** the preview open large over the page (owner 2026-10-12) */
  zoomed = false;

  @HostListener( 'document:keydown.escape' )
  closeZoom(): void {
    this.zoomed = false;
    this.styleOpen = false;
    this.modeOpen = false;
  }
  private lastSignature = '';
  private renderTimer: any = null;
  private ready = false;

  constructor( private wallpaperService: WallpaperService ) {
  }

  // Only a change to what the picture is made of redraws it. `subtitle` (and other object inputs)
  // are new references on every host change detection; redrawing on those looped: render → emit
  // preview → host CD → new subtitle → ngOnChanges → render... which kept the page busy and made
  // the dropdowns feel like they "went somewhere else" on every click.
  ngOnChanges( _changes: SimpleChanges ): void {
    const sig = this.signature();
    if ( sig !== this.lastSignature ) {
      this.lastSignature = sig;
      this.scheduleRender();
    }
  }

  private signature(): string {
    return [ this.palette.map( p => p.value ).join( ',' ), this.style, this.sizeKey, this.seed, this.params.join( '-' ) ].join( '|' );
  }

  ngAfterViewInit(): void {
    this.ready = true;
    this.scheduleRender();
  }

  ngOnDestroy(): void {
    if ( this.renderTimer ) clearTimeout( this.renderTimer );
  }

  get size() {
    return this.wallpaperService.sizeOf( this.sizeKey );
  }

  /** the picture in the preview and the style button: the host's, else the widget's own */
  get shownPreview(): string {
    return this.previewImage || this.miniUrl;
  }

  /** the current style's two fields (labels) */
  get styleInfo() {
    return styleInfoOf( this.style );
  }

  onParamInput( index: number, el: HTMLInputElement ): void {
    const max = this.styleInfo.params[ index ].max;
    let v = ( el.value || '' ).replace( /[^0-9]/g, '' ).slice( 0, 3 );
    if ( v !== '' && parseInt( v, 10 ) > max ) { v = String( max ); }
    el.value = v;
  }

  commitParam( index: number, raw: string ): void {
    const max = this.styleInfo.params[ index ].max;
    const n = parseInt( ( raw || '' ).replace( /[^0-9]/g, '' ), 10 );
    if ( isNaN( n ) ) return;
    const value = Math.max( 0, Math.min( max, n ) );
    if ( value === this.params[ index ] ) return;
    const next = [ ...this.params ];
    next[ index ] = value;
    this.params = next;
    this.paramsChange.emit( next );
    this.scheduleRender();
  }

  get styleLabel(): string {
    return ( WALLPAPER_STYLES.find( s => s.key === this.style ) || WALLPAPER_STYLES[ 0 ] ).label;
  }

  /** the palette's own tile row lives in the palette widget; an in-place edit there re-renders here */
  onEditColor( change: { index: number; value: string } ): void {
    this.editColor.emit( change );
    this.scheduleRender();
  }

  onStyleChange( value: string ): void {
    this.styleChange.emit( this.wallpaperService.styleOf( value ) );
  }

  /** coalesces a burst of input changes into one draw */
  private scheduleRender(): void {
    if ( !this.ready ) return;
    if ( this.renderTimer ) clearTimeout( this.renderTimer );
    this.renderTimer = setTimeout( () => {
      this.renderTimer = null;
      this.render();
    }, 30 );
  }

  private render(): void {
    const canvas = this.canvasRef?.nativeElement;
    if ( !canvas || !this.palette.length ) return;
    const size = this.size;
    const w = Math.min( PREVIEW_MAX_W, size.width );
    canvas.width = w;
    canvas.height = Math.round( w * size.height / size.width );
    this.wallpaperService.render( canvas, {
      style: this.style, colors: this.palette.map( p => p.value ), seed: this.seed, params: this.params,
    } );
    try {
      this.previewUrl = canvas.toDataURL( 'image/jpeg', 0.88 );
    } catch {
      this.previewUrl = '';
    }
    this.rendered.emit( this.previewUrl );
    this.renderHero();
  }

  /** the 1920 × 1080 framing, whatever size is chosen: the hero backdrop and the card's mini preview */
  private renderHero(): void {
    const canvas = document.createElement( 'canvas' );
    canvas.width = 1920;
    canvas.height = 1080;
    try {
      this.wallpaperService.render( canvas, {
        style: this.style, colors: this.palette.map( p => p.value ), seed: this.seed, params: this.params,
      } );
      this.miniUrl = canvas.toDataURL( 'image/jpeg', 0.88 );
    } catch {
      this.miniUrl = '';
    }
    this.heroRendered.emit( this.miniUrl );
  }
}
